import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { DEPOSIT_TYPE_LABELS, type DepositLog } from '@/api/supplier'

interface DepositLogTableProps {
	logs: DepositLog[]
	isLoading: boolean
}

// 金额格式化
function formatAmount(amount: number): string {
	return amount.toLocaleString('zh-CN', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})
}

// 日期格式化
function formatDate(dateStr: string): string {
	const d = new Date(dateStr)
	const yyyy = d.getFullYear()
	const mm = String(d.getMonth() + 1).padStart(2, '0')
	const dd = String(d.getDate()).padStart(2, '0')
	const hh = String(d.getHours()).padStart(2, '0')
	const mi = String(d.getMinutes()).padStart(2, '0')
	return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
}

// 变更类型对应的 Badge 样式
function getTypeBadgeVariant(type: string): 'default' | 'success' | 'destructive' | 'warning' | 'info' {
	switch (type) {
		case 'order_deduct':
			return 'destructive'
		case 'order_refund':
			return 'success'
		case 'admin_deposit':
			return 'info'
		case 'admin_deduct':
			return 'warning'
		default:
			return 'default'
	}
}

export function DepositLogTable({ logs, isLoading }: DepositLogTableProps) {
	if (isLoading) {
		return (
			<div className="rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[180px]">时间</TableHead>
							<TableHead className="w-[120px]">变更类型</TableHead>
							<TableHead className="w-[140px] text-right">变更金额</TableHead>
							<TableHead className="w-[140px] text-right">变更后余额</TableHead>
							<TableHead>备注</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Array.from({ length: 5 }).map((_, i) => (
							<TableRow key={i}>
								<TableCell><Skeleton className="h-4 w-[140px]" /></TableCell>
								<TableCell><Skeleton className="h-5 w-[80px]" /></TableCell>
								<TableCell><Skeleton className="h-4 w-[100px] ml-auto" /></TableCell>
								<TableCell><Skeleton className="h-4 w-[100px] ml-auto" /></TableCell>
								<TableCell><Skeleton className="h-4 w-[160px]" /></TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>
		)
	}

	if (logs.length === 0) {
		return (
			<div className="rounded-lg border">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="w-[180px]">时间</TableHead>
							<TableHead className="w-[120px]">变更类型</TableHead>
							<TableHead className="w-[140px] text-right">变更金额</TableHead>
							<TableHead className="w-[140px] text-right">变更后余额</TableHead>
							<TableHead>备注</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						<TableRow>
							<TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
								暂无变动记录
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</div>
		)
	}

	return (
		<div className="rounded-lg border">
			<Table>
				<TableHeader>
					<TableRow>
						<TableHead className="w-[180px]">时间</TableHead>
						<TableHead className="w-[120px]">变更类型</TableHead>
						<TableHead className="w-[140px] text-right">变更金额</TableHead>
						<TableHead className="w-[140px] text-right">变更后余额</TableHead>
						<TableHead>备注</TableHead>
					</TableRow>
				</TableHeader>
				<TableBody>
					{logs.map((log) => {
						const isPositive = log.amount > 0
						return (
							<TableRow key={log.id}>
								<TableCell className="text-sm text-muted-foreground">
									{formatDate(log.created_at)}
								</TableCell>
								<TableCell>
									<Badge variant={getTypeBadgeVariant(log.type)}>
										{DEPOSIT_TYPE_LABELS[log.type] || log.type}
									</Badge>
								</TableCell>
								<TableCell className="text-right">
									<span className={cn(
										'font-mono font-medium',
										isPositive ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400',
									)}>
										{isPositive ? '+' : ''}{formatAmount(log.amount)}
									</span>
								</TableCell>
								<TableCell className="text-right">
									<span className="font-mono text-muted-foreground">
										¥{formatAmount(log.balance_after)}
									</span>
								</TableCell>
								<TableCell className="text-sm text-muted-foreground">
									{log.remark || '-'}
								</TableCell>
							</TableRow>
						)
					})}
				</TableBody>
			</Table>
		</div>
	)
}
